/**
 * Depth scale shared by the editor's ghost silhouette and the player (§8.2).
 *
 *   s(y)          = clamp((y − horizon_y) / (reference.at_y − horizon_y), clamp.min, clamp.max)
 *   height_pct(a) = a.height_cm × (reference.height_pct / reference.cm) × s(feet_y)
 *
 * `bands` mode is piecewise-linear through `{y, scale}` (clamped to the end
 * values); `flat` is 1. Sky/fly regions with `scale_ref: {fixed_y}` evaluate
 * `s(y_fixed)` instead of the actor's own y.
 */

export interface DepthReference {
  cm: number
  at_y: number
  height_pct: number
}

export interface DepthClamp {
  min: number
  max: number
}

export type DepthLike =
  | { mode: 'horizon'; horizon_y?: number; reference?: DepthReference; clamp?: DepthClamp }
  | { mode: 'bands'; bands?: { y: number; scale: number }[]; clamp?: DepthClamp }
  | { mode: 'flat'; clamp?: DepthClamp }

export const DEFAULT_REFERENCE: DepthReference = { cm: 100, at_y: 92, height_pct: 34 }
export const DEFAULT_CLAMP: DepthClamp = { min: 0.2, max: 1.6 }
export const DEFAULT_HORIZON_Y = 42

const clampNum = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)

/** `s(y)`: the depth scale factor for feet at row `y` (percent of height). */
export function scaleAt(y: number, depth: DepthLike): number {
  const clamp = depth.clamp ?? DEFAULT_CLAMP
  switch (depth.mode) {
    case 'flat':
      return 1
    case 'horizon': {
      const hy = depth.horizon_y ?? DEFAULT_HORIZON_Y
      const ref = depth.reference ?? DEFAULT_REFERENCE
      const denom = ref.at_y - hy
      if (Math.abs(denom) < 1e-9) return clampNum(1, clamp.min, clamp.max)
      return clampNum((y - hy) / denom, clamp.min, clamp.max)
    }
    case 'bands': {
      const bands = [...(depth.bands ?? [])].sort((a, b) => a.y - b.y)
      if (bands.length === 0) return 1
      const first = bands[0]!
      const last = bands[bands.length - 1]!
      if (y <= first.y) return clampNum(first.scale, clamp.min, clamp.max)
      if (y >= last.y) return clampNum(last.scale, clamp.min, clamp.max)
      for (let i = 0; i + 1 < bands.length; i++) {
        const a = bands[i]!
        const b = bands[i + 1]!
        if (y >= a.y && y <= b.y) {
          const t = b.y === a.y ? 0 : (y - a.y) / (b.y - a.y)
          return clampNum(a.scale + (b.scale - a.scale) * t, clamp.min, clamp.max)
        }
      }
      return clampNum(last.scale, clamp.min, clamp.max)
    }
  }
}

/** Rendered height, in percent of the stage height, of a `heightCm`-tall thing standing at `feetY`. */
export function heightPctAt(heightCm: number, feetY: number, depth: DepthLike): number {
  const ref = depth.mode === 'horizon' ? (depth.reference ?? DEFAULT_REFERENCE) : DEFAULT_REFERENCE
  return heightCm * (ref.height_pct / ref.cm) * scaleAt(feetY, depth)
}

/**
 * Sprite scale relative to the sheet: `(height_pct / 100 × frameH) / body_px`
 * where `body_px = sheetFrameH × bbox_h / 100`.
 */
export function spriteScale(heightCm: number, feetY: number, depth: DepthLike, sheetFrameH: number, bboxHPct: number, stageH = 1080): number {
  const bodyPx = sheetFrameH * (bboxHPct / 100)
  if (bodyPx <= 0) return 1
  return (heightPctAt(heightCm, feetY, depth) / 100) * stageH / bodyPx
}

/** The `y` below which characters are still ≥ `minScale` tall (the "far scale tiny" lint helper). */
export function yForScale(target: number, depth: DepthLike): number | null {
  if (depth.mode !== 'horizon') return null
  const hy = depth.horizon_y ?? DEFAULT_HORIZON_Y
  const ref = depth.reference ?? DEFAULT_REFERENCE
  return hy + target * (ref.at_y - hy)
}
